import React, { useEffect, useState } from "react";
// 1. ADDED: useNavigate for redirection
import { useNavigate } from 'react-router-dom';
// 2. ADDED: Lock and ChevronRight icons
import { MapPin, Phone, Search, User, ChevronDown, ChevronUp, X, Lock, ChevronRight } from "lucide-react";

import styles from "./RightSidebar.module.css";

import EmptyProfileImage from '../../../../../images/navbar/empty-profile-image.png';
import { getBusinessProfileDataWithBID } from "../../../Services/getBusinessProfileDataWithBID";
import { getSearchMessages } from "../../../Services/getSearchMessages";
import StarRating from "../../../../StarRating/startRating";
import BusinessProfilePureCSS from '../../../../../profiles/BusinessProfilePureCSS';

// Helper functions 
const formatAddress = (model) => {
    const addressParts = [
        model.streetNumber, model.streetName, model.suburb, model.myState, model.postCode
    ];
    return addressParts.filter(part => part).join(', ');
};

const formatReviewText = (count) => {
    if (!count) return '';
    return `(${count} review${count !== 1 ? 's' : ''})`;
};

function RightSidebar({ conversationSearchId, setChatSearching, activeConversation, business }) {
    const navigate = useNavigate(); 
    const [businessProfile, setBusinessProfile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isBusinessProfileOpen, setIsBusinessProfileOpen] = useState(false);
    const [isBioExpanded, setIsBioExpanded] = useState(false);
    const [isSearchActive, setIsSearchActive] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [filteredMessages, setFilteredMessages] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchAttempted, setSearchAttempted] = useState(false);

    useEffect(() => {
        if (activeConversation && business?.businessId) {
            const fetchBusinessData = async () => {
                setLoading(true);
                setError(null);
                setBusinessProfile(null);
                setIsBioExpanded(false);
                setIsSearchActive(false);
                setFilteredMessages([]);
                setSearchText("");
                setSearchAttempted(false);

                try {
                    const response = await getBusinessProfileDataWithBID(business.businessId);
                    if (response.model) {
                        setBusinessProfile({
                            name: response.model.businessName,
                            profilePic: response.model.profilePic,
                            address: formatAddress(response.model),
                            phone: response.model.phone,
                            overallRating: response.model.overallRating,
                            reviewCount: response.model.reviewCount,
                            googleRating: response.model.googleRating,
                            googleRatingCount: response.model.googleRatingCount,
                            description: response.model.businessDescription,
                            bio: response.model.businessBio,
                            isVerified: response.model.isVerified
                        });
                    } else { throw new Error("Business data not found."); }
                } catch (err) {
                    console.error("Failed to fetch business profile:", err);
                    setError("Could not load business details.");
                } finally { setLoading(false); }
            };

            fetchBusinessData();
        }
    }, [activeConversation, business?.businessId]);

    // ... Search Handlers (Unchanged) ...
    const handleToggleSearch = () => {
        setIsSearchActive(!isSearchActive);
        setFilteredMessages([]);
        setSearchText("");
        setSearchAttempted(false);
    };

    const handleSearchSubmit = async (e) => {
        e.preventDefault();
        if (!searchText.trim()) return;
        setSearchLoading(true);
        setSearchAttempted(true);
        try {
            const results = await getSearchMessages(conversationSearchId, searchText);
            setFilteredMessages(results?.searchResponse || []);
        } catch (error) {
            console.error("Failed to fetch search results:", error);
            setFilteredMessages([]);
        } finally {
            setSearchLoading(false);
        }
    };

    const BIO_MAX_LENGTH = 120;
    const isBioLong = businessProfile?.bio && businessProfile.bio.length > BIO_MAX_LENGTH;
    
    const getBioText = () => {
        if (!businessProfile?.bio) return '';
        if (isBioLong && !isBioExpanded) {
            return `${businessProfile.bio.substring(0, BIO_MAX_LENGTH)}...`;
        }
        return businessProfile.bio;
    };

    // --- NEW: MASKING LOGIC HELPERS ---
    const handleEnquiryRedirect = () => {
        // Navigate to your enquiry page with the business ID
        navigate(`/contact-business?businessId=${business.businessId}`);
    };

    const getMaskedAddress = (fullAddress) => {
        if (!fullAddress) return 'Location Hidden';
        const postcodeMatch = fullAddress.match(/\b\d{4}\b/); 
        const stateMatch = fullAddress.match(/\b(NSW|VIC|QLD|WA|SA|TAS|NT|ACT)\b/i);
        const state = stateMatch ? stateMatch[0].toUpperCase() : '';
        const postcode = postcodeMatch ? postcodeMatch[0] : '';
        if (state || postcode) return `****, ${state} ${postcode}`;
        return '**** (Address Protected)';
    };

    const getMaskedPhone = (phone) => {
        if (!phone) return '04** *** ***';
        return `${phone.substring(0, 4)} *** ***`; 
    };

    if (!activeConversation) return null;

    return (
        <>
            <div className={styles.sidebarContainer}>
                <div className={styles.profileCard}>
                    {loading && <p className={styles.infoText}>Loading profile...</p>}
                    {error && <p className={styles.errorText}>{error}</p>}
                    
                    {businessProfile && (
                        <>
                            {/* Profile Header (Unchanged) */}
                            <div className={styles.profileHeader}>
                                <div className={styles.headerActions}>
                                    <button 
                                        type="button"
                                        onClick={handleToggleSearch} 
                                        className={styles.headerActionButton} 
                                        title={isSearchActive ? "Close Search" : "Search Messages"}
                                    >
                                        {isSearchActive ? <X size={20} /> : <Search size={20} />}
                                    </button>
                                    <button 
                                        type="button"
                                        onClick={() => setIsBusinessProfileOpen(true)} 
                                        className={styles.headerActionButton} 
                                        title="View Profile"
                                    >
                                        <User size={20} />
                                    </button>
                                </div>

                                <img 
                                    src={businessProfile.profilePic || EmptyProfileImage} 
                                    alt={`${businessProfile.name} profile`} 
                                    className={styles.profileImage}
                                />
                                <h3 className={styles.businessName} title={businessProfile.name}>
                                    {businessProfile.name}
                                </h3>
                                {businessProfile.description && !isSearchActive && (
                                    <p className={styles.description}>"{businessProfile.description}"</p>
                                )}
                            </div>

                            {/* Search Panel (Unchanged) */}
                            <div className={`${styles.searchPanel} ${isSearchActive ? styles.searchPanelActive : ''}`}>
                                <div className={styles.searchHeader}>
                                    <h4 className={styles.sectionTitle}>Search Conversation</h4>
                                    <button onClick={handleToggleSearch} className={styles.searchCloseButton}>
                                        <X size={18} />
                                    </button>
                                </div>
                                <form onSubmit={handleSearchSubmit}>
                                    <div className={styles.searchInputContainer}>
                                        <Search size={18} className={styles.searchInputIcon} />
                                        <input
                                            type="text"
                                            placeholder="Search messages..."
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            className={styles.searchInput}
                                        />
                                    </div>
                                </form>
                                <div className={styles.searchResultsList}>
                                    {searchLoading && <p className={styles.infoText}>Searching...</p>}
                                    {!searchLoading && searchAttempted && filteredMessages.length === 0 && (
                                        <p className={styles.infoText}>No messages found.</p>
                                    )}
                                    {filteredMessages.map((msg) => (
                                        <div 
                                            key={msg.messageId} 
                                            className={styles.searchResultItem}
                                            onClick={() => {
                                                setChatSearching(msg);
                                                handleToggleSearch();
                                            }}
                                        >
                                            <div className={styles.resultHeader}>
                                                <span className={styles.resultUser}>{msg.businessUserName || msg.homeUserName}</span>
                                                <span className={styles.resultDate}>{new Date(msg.messageTime).toLocaleDateString()}</span>
                                            </div>
                                            <p className={styles.resultMessage}>{msg.message}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className={`${styles.profileContent} ${isSearchActive ? styles.profileContentHidden : ''}`}>
                                {/* Ratings (Unchanged) */}
                                <div className={styles.ratingsSection}>
                                    <div className={styles.ratingItem}>
                                        <StarRating ratings={businessProfile.overallRating} />
                                        <span className={styles.ratingText}>
                                            {businessProfile.overallRating?.toFixed(1)} {formatReviewText(businessProfile.reviewCount)}
                                        </span>
                                    </div>
                                    {businessProfile.googleRating && businessProfile.googleRatingCount > 0 && (
                                        <div className={styles.ratingItem}>
                                            <span className={styles.googleRatingText}>
                                                Google Rating: {businessProfile.googleRating.toFixed(1)} {formatReviewText(businessProfile.googleRatingCount)}
                                            </span>
                                        </div>
                                    )}
                                </div>
                                
                                {/* Bio (Unchanged) */}
                                {businessProfile.bio && (
                                    <div className={styles.bioSection}>
                                        <h4 className={styles.sectionTitle}>About Us</h4>
                                        <p className={styles.bioText}>{getBioText()}</p>
                                        {isBioLong && (
                                            <button 
                                                className={styles.readMoreButton} 
                                                onClick={() => setIsBioExpanded(!isBioExpanded)}
                                            >
                                                {isBioExpanded ? 'Show Less' : 'Read More'}
                                                {isBioExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                            </button>
                                        )}
                                    </div>
                                )}

                                {/* --- REPLACED: CONTACT SECTION WITH LEAD TRAP --- */}
                                <div className={styles.contactSection}>
                                    <div className={styles.sectionHeader}>
                                        <h4 className={styles.sectionTitle}>Contact Information</h4>
                                        {!businessProfile.isVerified && (
                                            <span className={styles.privacyBadge}>
                                                <Lock size={12} /> Privacy Protected
                                            </span>
                                        )}
                                    </div>

                                    <div className={styles.contactList}>
                                        {/* ADDRESS */}
                                        {businessProfile.address && (
                                            <div 
                                                className={`${styles.contactItem} ${!businessProfile.isVerified ? styles.lockedItem : ''}`}
                                                onClick={!businessProfile.isVerified ? handleEnquiryRedirect : undefined}
                                            >
                                                <MapPin size={18} className={styles.contactIcon} />
                                                <span className={styles.contactText}>
                                                    {businessProfile.isVerified ?

                                                    (
                                                        <a 
                                                            href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(businessProfile.address)}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className={styles.contactLink}
                                                            title="Get Directions on Google Maps"
                                                        >
                                                            {businessProfile.address}
                                                        </a>
                                                    )
                                                        : 
                                                    (
                                                        getMaskedAddress(businessProfile.address)
                                                    )

                                                    }
                                                </span>
                                                {!businessProfile.isVerified && <ChevronRight size={16} className={styles.actionArrow} />}
                                            </div>
                                        )}

                                        {/* PHONE */}
                                        {businessProfile.phone && (
                                            <div 
                                                className={`${styles.contactItem} ${!businessProfile.isVerified ? styles.lockedItem : ''}`}
                                                onClick={!businessProfile.isVerified ? handleEnquiryRedirect : undefined}
                                            >
                                                <Phone size={18} className={styles.contactIcon} />
                                                {businessProfile.isVerified ? (
                                                    <a href={`tel:${businessProfile.phone}`} className={styles.contactLink}>
                                                        {businessProfile.phone}
                                                    </a>
                                                ) : (
                                                    <span className={styles.contactText}>
                                                        {getMaskedPhone(businessProfile.phone)}
                                                    </span>
                                                )}
                                                {!businessProfile.isVerified && <ChevronRight size={16} className={styles.actionArrow} />}
                                            </div>
                                        )}
                                    </div>

                                    {/* CTA FOR UNVERIFIED */}
                                    {!businessProfile.isVerified && (
                                        <div className={styles.enquiryWrapper}>
                                            <button 
                                                onClick={handleEnquiryRedirect}
                                                className={styles.btnFullEnquiry}
                                            >
                                                Make an Enquiry
                                            </button>
                                            <p className={styles.helperText}>
                                                Connect securely to reveal details.
                                            </p>
                                        </div>
                                    )}
                                </div>

                            </div>
                        </>
                    )}
                </div>
            </div>
            
            <BusinessProfilePureCSS
                isOpen={isBusinessProfileOpen}
                onClose={() => setIsBusinessProfileOpen(false)}
                businessId={business?.businessId}
                useIDTYPE={"BID"}
                showMessageButton={false}
            />
        </>
    );
}

export default RightSidebar;